import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import useRouter from 'utils/useRouter'
import { Card, CardContent, CircularProgress, Divider, Grid, TextField, Typography } from '@material-ui/core'
import { Page, Header } from 'components'
import useStyles from './styles'
import { Editor } from '@tinymce/tinymce-react'
import { useTipoExameService } from 'hooks/useTipoExameService'
import { useFormState } from 'hooks/useFormState'
import { BackButton } from 'components/BackButton/BackButton'
import { ButtonWithLoading } from 'components/ButtonWithLoading'
import constants from 'utils/constants'
import { useAuth } from 'hooks/useAuth'

const schema = {
  titulo: {
    presence: { allowEmpty: false, message: '^Título é obrigatório' },
  },
  textoDefault: {
    presence: { allowEmpty: false, message: '^Texto default é obrigatório' },
  },
}

export const FormularioSecao = (props) => {
  const { route } = props
  const router = useRouter()
  const tipoExameService = useTipoExameService()
  const editorRef = useRef(null)
  const classes = useStyles()
  const idSecaoExame = useSelector((state) => state.idsState.idSecaoExame)
  const idTipoExame = useSelector((state) => state.idsState.idExame)
  const [secao, setSecao] = useState(null)
  const [processandoSecao, setProcessandoSecao] = useState(false)
  const [processandoOperacao, setProcessandoOperacao] = useState(false)
  const [isFormError, setIsFormError] = useState(false)
  const { permissoesUsuario, verificarPermissao } = useAuth()

  useEffect(() => {

    if (permissoesUsuario?.length) {
      if(!verificarPermissao([constants.PERMISSOES.ADMINISTRAR_SISTEMA])) {
        router.history.push(`/`)
      }
    }
  }, [permissoesUsuario])

  const fetchSecaoExame = useCallback(async () => {
    if (idSecaoExame) {
      const response = await tipoExameService.listarSecaoTipoExameAsync(idSecaoExame, setProcessandoSecao)
      setSecao(response)
    }
  }, [idSecaoExame])

  const formSecao = useFormState(schema, {
    titulo: null,
    textoDefault: null,
  })

  const handleValueChange = (e) => {
    if (editorRef.current) {
      formSecao.setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          textoDefault: e.target.getContent(),
        },
      }))
    }
  }

  const handleClickConfirmar = useCallback(async () => {
    if (route.editar) {
      await tipoExameService.editarSecaoTipoExame(idSecaoExame, formSecao.formState.values, setProcessandoOperacao)
    } else {
      await tipoExameService.cadastrarSecaoTipoExame(idTipoExame, formSecao.formState.values, setProcessandoOperacao)
    }
    router.history.push('/exames/secoes')
  }, [formSecao.formState.values, idSecaoExame])

  useEffect(() => {
    if (route.editar) {
      fetchSecaoExame()
    }
  }, [fetchSecaoExame, route])

  useEffect(() => {
    if (secao?.id) {
      formSecao.setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          titulo: secao.titulo,
          textoDefault: secao.textoDefault,
        },
      }))
    }
  }, [secao])

  useEffect(() => {
    if (formSecao.hasErrorWithoutTouch('titulo') || formSecao.hasErrorWithoutTouch('textoDefault')) {
      setIsFormError(true)
    } else {
      setIsFormError(false)
    }
  }, [formSecao.formState])

  return (
    <Page className={classes.root} title={`${route.titulo} - SIGEP`}>
      <Header titulo={route.titulo} overline={route.secao} />
      <Card className={classes.results}>
        <CardContent>
          {processandoSecao ? (
            <div className={classes.carregando}>
              <CircularProgress disableShrink />
            </div>
          ) : (
            <Grid container md={12} sm={12} xs={12} justifyContent="space-between">
              <Grid item md={12} sm={12} xs={12}>
                <Typography>
                  <b>DADOS DA SEÇÃO DO DOCUMENTO</b>
                </Typography>
                <Divider style={{ marginTop: '1px' }} />
              </Grid>
              <Grid item container md={12} sm={12} xs={12} justifyContent="space-between" className={classes.formContent}>
                <Grid item md={12} sm={12} xs={12}>
                  <TextField
                    label="TÍTULO"
                    value={formSecao.formState.values.titulo || null}
                    onChange={formSecao.handleChange}
                    fullWidth
                    name="titulo"
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </Grid>

              <Grid item md={12} sm={12} xs={12} style={{ padding: '20px' }}>
                <Typography>
                  <b>TEXTO DEFAULT</b>
                </Typography>
                <Editor
                  tinymceScriptSrc='/tinymce/tinymce.min.js'
                  licenseKey="gpl"
                  onInit={(_, editor) => (editorRef.current = editor)}
                  initialValue={secao?.textoDefault || ''}
                  onChange={handleValueChange}
                  init={{
                    style_formats: [
                      {
                        title: 'Indent',
                        selector: 'p',
                        styles: { 'text-indent': '30px' },
                      },
                    ],
                    height: 500,
                    style_formats_merge: true,
                    relative_urls: false,
                    remove_script_host: false,
                    convert_urls: true,
                    pagebreak_separator: '<p style="page-break-after: always;">&nbsp;</p>',
                    menubar: false,
                    plugins: ['advlist autolink lists link image charmap print preview anchor', 'searchreplace visualblocks code fullscreen', 'insertdatetime media table paste code help wordcount pagebreak'],
                    toolbar: 'undo redo | formatselect | table | ' + 'bold italic backcolor | alignleft aligncenter ' + 'alignright alignjustify | bullist numlist | ' + 'removeformat | help | pagebreak',
                    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                  }}
                />
              </Grid>
              <Grid item md={12} sm={12} xs={12}>
                <Divider />
              </Grid>
              <Grid item container md={12} sm={12} xs={12} justifyContent="space-between">
                <Grid item>
                  <BackButton size="small" />
                </Grid>
                <Grid item>
                  <ButtonWithLoading variant="contained" isLoading={processandoOperacao} isDisabled={isFormError} label="Confirmar" color="secondary" onClick={handleClickConfirmar} />
                </Grid>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    </Page>
  )
}
